/**
 * EstoqueReportPage — relatório PDF/XLSX de saldo de estoque (módulo Relatórios).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Download,
  FileSpreadsheet,
  Search,
  Package,
  Tag,
  X,
  CheckCircle,
  Loader2,
  ChevronDown,
} from 'lucide-react'
import * as XLSX from 'xlsx'
import EstoqueReportPdfCanvas, {
  ESTOQUE_COLUMN_DEFS,
  DEFAULT_ESTOQUE_COLUMNS,
  buildEstoqueExcelRows,
} from '../../../../components/ERP/Relatorios/EstoqueReportPdfCanvas'
import '../../../../components/ERP/Financeiro/AccountsReceivablePdfCanvas.css'
import { exportElementToPdf, exportElementsToPdf } from '../../../../utils/exportElementToPdf'
import { productService, brandService } from '../../../../services/erpApi'
import Toast from '../../../../components/Toast/Toast'
import { useToast } from '../../../../hooks/useToast'
import { usePermissions, ACTIONS } from '../../../../hooks/usePermissions'
import { canAccessErpReport } from '../../../../utils/permissions'
import '../ContasReceber/ContasReceberReportPage.css'

const ACTIVE_OPTIONS = [
  { value: 'active', label: 'Ativos' },
  { value: 'inactive', label: 'Inativos' },
  { value: 'all', label: 'Todos' },
]

const STOCK_OPTIONS = [
  { value: 'all', label: 'Qualquer saldo' },
  { value: 'in_stock', label: 'Com disponível' },
  { value: 'out_of_stock', label: 'Sem disponível' },
  { value: 'below_min', label: 'Abaixo do mínimo' },
]

/** Evita canvas HTML maior que o navegador consegue renderizar (páginas brancas). */
const PDF_CHUNK_SIZE = 80

function chunkArray(items, size) {
  const chunks = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks.length ? chunks : [[]]
}

function applyClientStockFilter(products, stockFilter) {
  if (stockFilter === 'in_stock') {
    return products.filter((p) => (Number(p.stock_available) || 0) > 0)
  }
  if (stockFilter === 'out_of_stock') {
    return products.filter((p) => (Number(p.stock_available) || 0) <= 0)
  }
  if (stockFilter === 'below_min') {
    return products.filter((p) => {
      const min = Number(p.min_stock) || 0
      if (min <= 0) return false
      return (Number(p.stock_available) || 0) < min
    })
  }
  return products
}

function applyBrandFilter(products, selectedBrands) {
  if (!selectedBrands.length) return products
  const ids = new Set(selectedBrands.map((b) => String(b.id)))
  const names = new Set(
    selectedBrands.map((b) => String(b.name || '').trim().toLowerCase()).filter(Boolean)
  )
  return products.filter((p) => {
    if (p.brand_id != null && ids.has(String(p.brand_id))) return true
    const brandName = String(p.brand_name || p.brand || '').trim().toLowerCase()
    return brandName && names.has(brandName)
  })
}

function sortProducts(products) {
  return [...products].sort((a, b) => {
    const nameA = String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR')
    if (nameA !== 0) return nameA
    return String(a.sku || '').localeCompare(String(b.sku || ''), 'pt-BR')
  })
}

function EstoqueReportPage() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const canView = canAccessErpReport(hasPermission, ACTIONS.REPORTS_STOCK)
  const { toast, showToast, hideToast } = useToast()

  const pdfRef = useRef(null)
  const chunkRefs = useRef([])
  const [generatingPDF, setGeneratingPDF] = useState(false)
  const [generatingXlsx, setGeneratingXlsx] = useState(false)
  const [exportProducts, setExportProducts] = useState([])
  const [exportChunks, setExportChunks] = useState([])
  const [exportTotalsProducts, setExportTotalsProducts] = useState([])
  const [previewCount, setPreviewCount] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const isExporting = generatingPDF || generatingXlsx

  const [selectedBrands, setSelectedBrands] = useState([])
  const [brandModalOpen, setBrandModalOpen] = useState(false)
  const [brandSearchTerm, setBrandSearchTerm] = useState('')
  const [modalBrands, setModalBrands] = useState([])
  const [loadingBrands, setLoadingBrands] = useState(false)
  const [draftBrands, setDraftBrands] = useState([])

  const [visibleColumns, setVisibleColumns] = useState(() => [...DEFAULT_ESTOQUE_COLUMNS])

  const [filters, setFilters] = useState({
    search: '',
    activeMode: 'active',
    stockFilter: 'all',
  })

  const brandLabel = useMemo(() => {
    if (!selectedBrands.length) return 'Todas as marcas'
    if (selectedBrands.length === 1) return selectedBrands[0].name
    return `${selectedBrands.length} marcas selecionadas`
  }, [selectedBrands])

  const pdfFilters = useMemo(
    () => ({
      search: filters.search,
      active:
        filters.activeMode === 'active'
          ? true
          : filters.activeMode === 'inactive'
            ? false
            : undefined,
      stockFilter: filters.stockFilter,
      selectedBrandNames: selectedBrands.map((b) => b.name),
    }),
    [filters, selectedBrands]
  )

  useEffect(() => {
    if (!brandModalOpen) return

    const load = async () => {
      setLoadingBrands(true)
      try {
        const params = brandSearchTerm.trim()
          ? { search: brandSearchTerm.trim() }
          : {}
        const response = await brandService.getAll(params)
        if (response.success && Array.isArray(response.data) && response.data.length > 0) {
          setModalBrands(response.data)
          return
        }

        // Fallback: extrai marcas dos produtos (caso sem permissão de marcas)
        const productsRes = await productService.getAll(
          filters.activeMode === 'active'
            ? { active: true }
            : filters.activeMode === 'inactive'
              ? { active: false }
              : {}
        )
        const rows = productsRes.success && Array.isArray(productsRes.data) ? productsRes.data : []
        const byId = new Map()
        rows.forEach((p) => {
          const id = p.brand_id
          const name = p.brand_name || p.brand
          if (id == null || !name) return
          byId.set(String(id), { id, name })
        })
        let brands = [...byId.values()].sort((a, b) =>
          String(a.name).localeCompare(String(b.name), 'pt-BR')
        )
        const term = brandSearchTerm.trim().toLowerCase()
        if (term) {
          brands = brands.filter((b) => String(b.name).toLowerCase().includes(term))
        }
        setModalBrands(brands)
      } catch (error) {
        console.error('Erro ao buscar marcas:', error)
        setModalBrands([])
      } finally {
        setLoadingBrands(false)
      }
    }

    const timeoutId = setTimeout(load, brandSearchTerm.trim() ? 300 : 0)
    return () => clearTimeout(timeoutId)
  }, [brandModalOpen, brandSearchTerm, filters.activeMode])

  useEffect(() => {
    if (!brandModalOpen) return undefined

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setBrandModalOpen(false)
        setBrandSearchTerm('')
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [brandModalOpen])

  const applyBrandSelection = useCallback((brands) => {
    setSelectedBrands(brands)
    setPreviewCount(null)
  }, [])

  const openBrandModal = () => {
    setDraftBrands(selectedBrands)
    setBrandSearchTerm('')
    setBrandModalOpen(true)
  }

  const closeBrandModal = () => {
    setBrandModalOpen(false)
    setBrandSearchTerm('')
  }

  const toggleDraftBrand = (brand) => {
    const exists = draftBrands.some((b) => String(b.id) === String(brand.id))
    const next = exists
      ? draftBrands.filter((b) => String(b.id) !== String(brand.id))
      : [...draftBrands, { id: brand.id, name: brand.name }]
    setDraftBrands(next)
    applyBrandSelection(next)
  }

  const selectAllBrands = () => {
    setDraftBrands([])
    applyBrandSelection([])
  }

  const confirmBrandSelection = () => {
    applyBrandSelection(draftBrands)
    closeBrandModal()
  }

  const toggleColumn = (columnId) => {
    setVisibleColumns((prev) => {
      if (prev.includes(columnId)) {
        if (prev.length === 1) return prev
        return prev.filter((id) => id !== columnId)
      }
      return [...prev, columnId]
    })
    setPreviewCount(null)
  }

  const selectAllColumns = () => {
    setVisibleColumns(ESTOQUE_COLUMN_DEFS.map((c) => c.id))
    setPreviewCount(null)
  }

  const resetDefaultColumns = () => {
    setVisibleColumns([...DEFAULT_ESTOQUE_COLUMNS])
    setPreviewCount(null)
  }

  const fetchProducts = useCallback(async () => {
    const apiFilters = {}
    if (filters.search.trim()) {
      apiFilters.search = filters.search.trim()
    }
    if (filters.activeMode === 'active') {
      apiFilters.active = true
    } else if (filters.activeMode === 'inactive') {
      apiFilters.active = false
    }
    if (filters.stockFilter === 'in_stock') {
      apiFilters.inStock = true
    }

    const response = await productService.getAll(apiFilters)
    if (!response.success) {
      throw new Error(response.message || 'Erro ao consultar produtos')
    }

    const rows = Array.isArray(response.data) ? response.data : []
    const filtered = applyBrandFilter(
      applyClientStockFilter(rows, filters.stockFilter),
      selectedBrands
    )
    return sortProducts(filtered)
  }, [filters, selectedBrands])

  const handlePreviewCount = async () => {
    setPreviewLoading(true)
    try {
      const products = await fetchProducts()
      setPreviewCount(products.length)
    } catch (err) {
      console.error(err)
      showToast(err.message || 'Erro ao consultar produtos', 'error')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleGeneratePdf = async () => {
    setGeneratingPDF(true)
    try {
      const products = await fetchProducts()

      if (!products.length) {
        showToast('Nenhum produto encontrado com os filtros atuais', 'warning')
        return
      }

      if (!visibleColumns.length) {
        showToast('Selecione ao menos uma coluna para o PDF', 'warning')
        return
      }

      setPreviewCount(products.length)
      const chunks = chunkArray(products, PDF_CHUNK_SIZE)
      const filename = `Relatorio_Estoque_${Date.now()}.pdf`
      const scale = products.length > 120 ? 1.5 : 2

      if (chunks.length === 1) {
        setExportProducts(products)
        setExportChunks([])
        setExportTotalsProducts([])
        await new Promise((resolve) => setTimeout(resolve, 250))

        if (!pdfRef.current) {
          throw new Error('Falha ao montar documento PDF')
        }

        await exportElementToPdf(pdfRef.current, filename, { scale })
      } else {
        setExportProducts([])
        setExportTotalsProducts(products)
        setExportChunks(chunks)
        chunkRefs.current = []
        await new Promise((resolve) => setTimeout(resolve, 350))

        const elements = chunkRefs.current.filter(Boolean)
        if (elements.length !== chunks.length) {
          throw new Error('Falha ao montar documento PDF em lotes')
        }

        await exportElementsToPdf(elements, filename, { scale })
      }

      showToast(`${products.length} produto(s) exportado(s) em PDF`, 'success')
    } catch (error) {
      console.error('Erro ao exportar PDF de estoque:', error)
      showToast(error.message || 'Erro ao gerar PDF. Tente novamente.', 'error')
    } finally {
      setGeneratingPDF(false)
      setExportProducts([])
      setExportChunks([])
      setExportTotalsProducts([])
      chunkRefs.current = []
    }
  }

  const handleGenerateXlsx = async () => {
    setGeneratingXlsx(true)
    try {
      const products = await fetchProducts()

      if (!products.length) {
        showToast('Nenhum produto encontrado com os filtros atuais', 'warning')
        return
      }

      if (!visibleColumns.length) {
        showToast('Selecione ao menos uma coluna para o Excel', 'warning')
        return
      }

      setPreviewCount(products.length)

      const exportData = buildEstoqueExcelRows(products, visibleColumns)
      const worksheet = XLSX.utils.json_to_sheet(exportData)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Estoque')

      const filename = `Relatorio_Estoque_${Date.now()}.xlsx`
      XLSX.writeFile(workbook, filename, { bookType: 'xlsx' })

      showToast(`${products.length} produto(s) exportado(s) em Excel`, 'success')
    } catch (error) {
      console.error('Erro ao exportar Excel de estoque:', error)
      showToast(error.message || 'Erro ao gerar Excel. Tente novamente.', 'error')
    } finally {
      setGeneratingXlsx(false)
    }
  }

  if (!canView) {
    return (
      <div className="cr-report-page">
        <p className="cr-report-denied">Você não tem permissão para este relatório.</p>
      </div>
    )
  }

  return (
    <div className="cr-report-page">
      <div className="cr-report-header">
        <div className="cr-report-header-left">
          <button
            type="button"
            className="cr-report-back"
            onClick={() => navigate('/erp/relatorios')}
            aria-label="Voltar"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="cr-report-title">Estoque</h1>
            <p className="cr-report-subtitle">Saldo atual por produto — PDF ou Excel</p>
          </div>
        </div>
        <div className="cr-report-header-actions">
          <button
            type="button"
            className="cr-report-btn-secondary"
            onClick={handleGenerateXlsx}
            disabled={isExporting}
          >
            <FileSpreadsheet size={16} />
            {generatingXlsx ? 'Gerando…' : 'Baixar Excel'}
          </button>
          <button
            type="button"
            className="cr-report-btn-primary"
            onClick={handleGeneratePdf}
            disabled={isExporting}
          >
            <Download size={16} />
            {generatingPDF ? 'Gerando…' : 'Gerar PDF'}
          </button>
        </div>
      </div>

      <div className="cr-report-card">
        <h2 className="cr-report-section-title">Filtros</h2>

        <div className="cr-report-field">
          <label htmlFor="st-search">Busca</label>
          <div className="cr-report-input-wrap">
            <Search size={16} className="cr-report-input-icon" />
            <input
              id="st-search"
              type="text"
              placeholder="Nome, SKU, código de barras…"
              value={filters.search}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, search: e.target.value }))
                setPreviewCount(null)
              }}
            />
          </div>
        </div>

        <div className="cr-report-field">
          <span className="cr-report-label">Marca</span>
          <button
            type="button"
            className={`cr-report-customer-trigger${selectedBrands.length ? ' has-value' : ''}`}
            onClick={openBrandModal}
          >
            <Tag size={16} className="cr-report-customer-trigger-icon" />
            <span className="cr-report-customer-trigger-text">{brandLabel}</span>
            <ChevronDown size={16} />
          </button>
        </div>

        <div className="cr-report-field">
          <span className="cr-report-label">Situação do produto</span>
          <div className="cr-report-chips">
            {ACTIVE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`cr-report-chip${filters.activeMode === opt.value ? ' is-active' : ''}`}
                onClick={() => {
                  setFilters((prev) => ({ ...prev, activeMode: opt.value }))
                  setPreviewCount(null)
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="cr-report-field">
          <span className="cr-report-label">Saldo</span>
          <div className="cr-report-chips">
            {STOCK_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`cr-report-chip${filters.stockFilter === opt.value ? ' is-active' : ''}`}
                onClick={() => {
                  setFilters((prev) => ({ ...prev, stockFilter: opt.value }))
                  setPreviewCount(null)
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="cr-report-field">
          <div className="cr-report-columns-header">
            <span className="cr-report-label">Colunas do relatório</span>
            <div className="cr-report-columns-actions">
              <button type="button" className="cr-report-link-btn" onClick={selectAllColumns}>
                Todas
              </button>
              <button type="button" className="cr-report-link-btn" onClick={resetDefaultColumns}>
                Padrão
              </button>
            </div>
          </div>
          <div className="cr-report-columns-grid">
            {ESTOQUE_COLUMN_DEFS.map((col) => {
              const checked = visibleColumns.includes(col.id)
              return (
                <label key={col.id} className={`cr-report-column-check${checked ? ' is-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleColumn(col.id)}
                  />
                  <span>{col.label}</span>
                </label>
              )
            })}
          </div>
          <p className="cr-report-hint">
            Custo/venda totais = preço × estoque físico. Validade e endereço vêm do último
            endereçamento/inspeção de entrada quando disponíveis.
          </p>
        </div>

        <div className="cr-report-actions">
          <button
            type="button"
            className="cr-report-btn-secondary"
            onClick={handlePreviewCount}
            disabled={previewLoading || isExporting}
          >
            {previewLoading ? 'Consultando…' : 'Contar produtos'}
          </button>
          {previewCount != null && (
            <span className="cr-report-count">
              {previewCount} produto{previewCount === 1 ? '' : 's'} com os filtros atuais
            </span>
          )}
        </div>
      </div>

      {brandModalOpen && (
        <div className="cr-report-modal-overlay" onClick={closeBrandModal}>
          <div
            className="cr-report-modal-content"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="st-brand-modal-title"
          >
            <div className="cr-report-modal-header">
              <div className="cr-report-modal-header-left">
                <div className="cr-report-modal-icon">
                  <Tag size={24} />
                </div>
                <div>
                  <h3 id="st-brand-modal-title" className="cr-report-modal-title">
                    Selecionar Marca
                  </h3>
                  <p className="cr-report-modal-subtitle">
                    Uma, várias ou todas as marcas
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeBrandModal}
                className="cr-report-modal-close"
                aria-label="Fechar"
              >
                <X size={24} />
              </button>
            </div>

            <div className="cr-report-modal-body">
              <div className="cr-report-search-box">
                <Search size={18} className="cr-report-search-icon" />
                <input
                  type="text"
                  className="cr-report-search-input"
                  placeholder="Buscar marca..."
                  value={brandSearchTerm}
                  onChange={(e) => setBrandSearchTerm(e.target.value)}
                  autoFocus
                />
                {loadingBrands && (
                  <div className="cr-report-search-loading">
                    <Loader2 size={16} className="cr-report-spinning" />
                  </div>
                )}
              </div>

              <button
                type="button"
                className={`cr-report-select-item cr-report-select-all${draftBrands.length === 0 ? ' selected' : ''}`}
                onClick={selectAllBrands}
              >
                <div className="cr-report-select-item-avatar">
                  <Tag size={20} />
                </div>
                <div className="cr-report-select-item-info">
                  <div className="cr-report-select-item-name">Todas as marcas</div>
                  <div className="cr-report-select-item-detail">
                    Inclui produtos de qualquer marca
                  </div>
                </div>
                {draftBrands.length === 0 && (
                  <CheckCircle size={20} className="cr-report-select-item-check" />
                )}
              </button>

              <div className="cr-report-select-list">
                {loadingBrands ? (
                  <div className="cr-report-select-empty">
                    <Loader2 size={32} className="cr-report-spinning" />
                    <p>Buscando marcas...</p>
                  </div>
                ) : modalBrands.length > 0 ? (
                  modalBrands.map((brand) => {
                    const selected = draftBrands.some(
                      (b) => String(b.id) === String(brand.id)
                    )
                    return (
                      <button
                        type="button"
                        key={brand.id}
                        className={`cr-report-select-item${selected ? ' selected' : ''}`}
                        onClick={() => toggleDraftBrand(brand)}
                      >
                        <div className="cr-report-select-item-avatar">
                          <Tag size={20} />
                        </div>
                        <div className="cr-report-select-item-info">
                          <div className="cr-report-select-item-name">{brand.name}</div>
                        </div>
                        {selected && (
                          <CheckCircle size={20} className="cr-report-select-item-check" />
                        )}
                      </button>
                    )
                  })
                ) : (
                  <div className="cr-report-select-empty">
                    <Tag size={48} />
                    <p>
                      {brandSearchTerm.trim()
                        ? 'Nenhuma marca encontrada'
                        : 'Nenhuma marca disponível'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="cr-report-modal-footer">
              <button
                type="button"
                className="cr-report-modal-btn-secondary"
                onClick={closeBrandModal}
              >
                Fechar
              </button>
              <button
                type="button"
                className="cr-report-modal-btn-primary"
                onClick={confirmBrandSelection}
              >
                {draftBrands.length === 0
                  ? 'Usar todas'
                  : `Confirmar (${draftBrands.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={hideToast} />

      {(generatingPDF || generatingXlsx) && (
        <div className="ar-pdf-overlay">
          <div className="ar-pdf-overlay-card">
            <Package size={28} color="#10b981" />
            <h4>{generatingXlsx ? 'Gerando Excel' : 'Gerando PDF'}</h4>
            <p>Preparando relatório de estoque...</p>
          </div>
        </div>
      )}

      {exportProducts.length > 0 && (
        <div className="ar-pdf-offscreen" aria-hidden>
          <EstoqueReportPdfCanvas
            ref={pdfRef}
            products={exportProducts}
            filters={pdfFilters}
            columns={visibleColumns}
          />
        </div>
      )}

      {exportChunks.length > 0 && (
        <div className="ar-pdf-offscreen" aria-hidden>
          {exportChunks.map((chunk, index) => (
            <EstoqueReportPdfCanvas
              key={`chunk-${index}`}
              ref={(el) => {
                chunkRefs.current[index] = el
              }}
              products={chunk}
              filters={pdfFilters}
              columns={visibleColumns}
              chunkMeta={{
                index,
                total: exportChunks.length,
                totalsProducts: exportTotalsProducts,
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default EstoqueReportPage
